/**
 * Pure colour helpers used by the HTML style editor — hex parsing/normalising,
 * tinting toward white, and luminance-based contrast text selection.
 *
 * Kept here (separate from rendering code) so the panel components remain easy to
 * test in isolation and so colour math can be unit-tested directly.
 */

export function parseHexChannels(hex: string): [number, number, number] | null {
  const h = hex.trim().replace(/^#/, '');
  if (h.length !== 6) {
    return null;
  }
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  if (Number.isNaN(r) || Number.isNaN(g) || Number.isNaN(b)) {
    return null;
  }
  return [r, g, b];
}

/** Mix hex toward white (ratio 0–1); returns original if not parseable. */
export function computeTint(hexColor: string, ratio: number): string {
  const rgb = parseHexChannels(hexColor);
  if (!rgb) {
    return hexColor;
  }
  const [r, g, b] = rgb;
  const tr = Math.round(r * ratio + 255 * (1 - ratio));
  const tg = Math.round(g * ratio + 255 * (1 - ratio));
  const tb = Math.round(b * ratio + 255 * (1 - ratio));
  return `#${tr.toString(16).padStart(2, '0')}${tg.toString(16).padStart(2, '0')}${tb.toString(16).padStart(2, '0')}`;
}

export function relativeLuminance(hexColor: string): number {
  const rgb = parseHexChannels(hexColor);
  if (!rgb) {
    return 0;
  }
  const linearize = (c: number) => {
    const s = c / 255;
    return s <= 0.04045 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  };
  const [r, g, b] = rgb;
  return 0.2126 * linearize(r) + 0.7152 * linearize(g) + 0.0722 * linearize(b);
}

/**
 * Picks a foreground text colour for the supplied background. Dark backgrounds
 * get white; lighter backgrounds keep the document's body text colour so brand
 * colours flow through tables and badges consistently.
 */
export function contrastTextColor(backgroundHex: string, bodyTextColor: string): string {
  return relativeLuminance(backgroundHex) < 0.3 ? '#ffffff' : bodyTextColor;
}

/** Returns "#rrggbb" (lowercase) for a valid 6-digit hex, or null for invalid/incomplete input. */
export function normalizeHex6(hex: string): string | null {
  const h = hex.trim().replace(/^#/, '');
  if (!/^[0-9a-fA-F]{6}$/.test(h)) {
    return null;
  }
  return `#${h.toLowerCase()}`;
}
